import { Button, Group, Modal, Stack, Text } from '@mantine/core'
import { FormattedMessage, useIntl } from 'react-intl'
import { extractErrorMessage } from '@/api/helpers/extractErrorMessage'
import {
  useDeleteAllCrossCameraPeople,
  useDeleteCrossCameraPerson
} from '@/queries/crossCameraQueries'
import { showToast } from '@/theme/notifications'
import { CrossCameraDeleteTarget } from '@/types/crossCamera'

const MESSAGES = {
  person: {
    title: 'crossCamera.people.delete.title',
    body: 'crossCamera.people.delete.body',
    success: 'crossCamera.people.delete.success',
    error: 'crossCamera.people.delete.error'
  },
  everyone: {
    title: 'crossCamera.people.deleteAll.title',
    body: 'crossCamera.people.deleteAll.body',
    success: 'crossCamera.people.deleteAll.success',
    error: 'crossCamera.people.deleteAll.error'
  }
}

const UNTRACKED_BODIES = {
  person: 'crossCamera.people.deleteUntracked.body',
  everyone: 'crossCamera.people.deleteAllUntracked.body'
}

type CrossCameraPersonDeleteModalProps = {
  appId?: string
  target: CrossCameraDeleteTarget | null
  onClose: () => void
}

export const CrossCameraPersonDeleteModal = ({
  appId,
  target,
  onClose
}: CrossCameraPersonDeleteModalProps) => {
  const intl = useIntl()
  const { mutateAsync: deletePerson, isPending: isDeletingPerson } =
    useDeleteCrossCameraPerson()
  const { mutateAsync: deleteEveryone, isPending: isDeletingEveryone } =
    useDeleteAllCrossCameraPeople()
  const kind = target && 'everyone' in target ? 'everyone' : 'person'
  const messages = MESSAGES[kind]
  const body = appId ? messages.body : UNTRACKED_BODIES[kind]

  const handleDeleteClick = async () => {
    if (!target) return

    try {
      if ('everyone' in target) {
        await deleteEveryone({ application_id: appId })
      } else {
        await deletePerson({
          application_id: target.appId,
          identity_id: target.identityId
        })
      }
      showToast(intl.formatMessage({ id: messages.success }), 'success')
      onClose()
    } catch (error) {
      showToast(
        extractErrorMessage(error, intl.formatMessage({ id: messages.error })),
        'error'
      )
    }
  }

  return (
    <Modal
      opened={target !== null}
      title={<FormattedMessage id={messages.title} />}
      onClose={onClose}
    >
      <Stack gap="md">
        <Text size="sm">
          <FormattedMessage id={body} />
        </Text>

        <Group justify="end">
          <Button variant="subtle" onClick={onClose}>
            <FormattedMessage id="cancel" />
          </Button>
          <Button
            color="red"
            loading={isDeletingPerson || isDeletingEveryone}
            onClick={() => void handleDeleteClick()}
          >
            <FormattedMessage id="delete" />
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}
